/**
 * RBAC — パーミッション定数とロールマトリクス（ARCHITECTURE.md §7.2 が唯一の正）。
 *
 * **ロール名の直接比較を禁止する。** ガードは必ず `hasPerm()` /
 * `requiredPermissions()` を通してパーミッション名で書く。
 * ロール名で分岐すると、ロールを 1 つ足したときに「どこを直すべきか」が
 * grep でしか分からなくなり、必ず取りこぼす。
 *
 * ops は **1 ユーザ 1 ロール**（和集合の計算を発生させない）。
 * portal は 1 ユーザが複数組織に別ロールで所属しうるが、リクエストのスコープは
 * JWT の `org` クレームで 1 組織に固定されるので、実効ロールは常に 1 つ。
 */
import type { OrgRole, StaffRole } from './enums.js';

/* ------------------------------------------------------------ ops 権限 */

export const OPS_PERMISSION = {
  nodeRead: 'ops.node.read',
  nodeWrite: 'ops.node.write',
  nodeDelete: 'ops.node.delete',
  nodeCommand: 'ops.node.command',

  orgRead: 'ops.org.read',
  orgWrite: 'ops.org.write',
  orgSuspend: 'ops.org.suspend',
  /** サポート窓口としてのメールボックスパスワード再設定。 */
  supportMailboxReset: 'ops.support.mailbox.reset',
  /**
   * 問い合わせ（サポートチケット）の閲覧・返信（A97 / A147）。
   * `supportMailboxReset`（顧客の資格情報を作り替える操作）とは重さが違うので
   * 分けてある。**読みは全ロールに開ける**（auditor が「苦情に期限内で答えたか」
   * を確かめられないと、体制の監査そのものが成り立たない）。
   */
  supportTicketRead: 'ops.support.ticket.read',
  supportTicketWrite: 'ops.support.ticket.write',
  /**
   * データ主体からの権利行使要求（開示・訂正・削除）の台帳（A134 / R59）。
   * 本人確認の可否と開示の判断は通信の秘密に触れるので、
   * `supportTicketWrite` とは別権限にして誰が触れるかを絞れるようにする。
   */
  privacyRequestWrite: 'ops.privacy.request.write',

  planRead: 'ops.plan.read',
  planWrite: 'ops.plan.write',
  billingRead: 'ops.billing.read',
  billingWrite: 'ops.billing.write',
  usageRead: 'ops.usage.read',

  jobRead: 'ops.job.read',
  jobWrite: 'ops.job.write',

  updateRead: 'ops.update.read',
  updatePublish: 'ops.update.publish',
  /** 進行中ロールアウトの緊急停止。support にも許す（事故を止める側は広く）。 */
  updateHalt: 'ops.update.halt',

  auditRead: 'ops.audit.read',
  auditVerify: 'ops.audit.verify',

  /** DKIM / TLS 秘密鍵の閲覧。`requireFreshAuth` + 同期監査が必須。 */
  secretRead: 'ops.secret.read',

  staffRead: 'ops.staff.read',
  staffWrite: 'ops.staff.write',

  impersonate: 'ops.impersonate',
  impersonateWrite: 'ops.impersonate.write',

  securityRead: 'ops.security.read',
  securityWrite: 'ops.security.write',

  backupRead: 'ops.backup.read',
  backupRestore: 'ops.backup.restore',

  webmailRead: 'ops.webmail.read',
  webmailWrite: 'ops.webmail.write',

  certRead: 'ops.cert.read',
  certWrite: 'ops.cert.write',

  settingsRead: 'ops.settings.read',
  settingsWrite: 'ops.settings.write',

  /** 進行中の大量配信の緊急停止。**止める操作は広く許す**（`updateHalt` と同じ思想）。 */
  bulkHalt: 'ops.bulk.halt',
  /** platform スコープのテンプレートの公開・撤回。 */
  contentTemplateWrite: 'ops.content.template.write',
  /**
   * 契約者のメール本文の閲覧。**superadmin のみ**かつ break-glass（二名承認 +
   * fresh-auth + 期限付き grant）を経た場合に限る。
   * 運営者アカウント 1 個の乗っ取りが全顧客のメール本文の漏洩にならないよう、
   * 日常運用のロール（operator / support）からは完全に外す。
   */
  contentReadBody: 'ops.content.read_body',
} as const;

export type OpsPermission = (typeof OPS_PERMISSION)[keyof typeof OPS_PERMISSION];

/* --------------------------------------------------------- portal 権限 */

export const ORG_PERMISSION = {
  read: 'org.read',
  write: 'org.write',

  memberRead: 'org.member.read',
  memberWrite: 'org.member.write',

  domainRead: 'org.domain.read',
  domainWrite: 'org.domain.write',

  mailboxRead: 'org.mailbox.read',
  mailboxWrite: 'org.mailbox.write',

  aliasRead: 'org.alias.read',
  aliasWrite: 'org.alias.write',

  logRead: 'org.log.read',
  securityRead: 'org.security.read',
  usageRead: 'org.usage.read',
  jobRead: 'org.job.read',

  billingRead: 'org.billing.read',
  billingManage: 'org.billing.manage',

  apiTokenRead: 'org.api_token.read',
  apiTokenWrite: 'org.api_token.write',

  exportWrite: 'org.export.write',

  /**
   * 問い合わせ（サポートチケット）の閲覧と起票（A97 / A147）。
   * `read` / `write`（組織設定の読み書き）に相乗りさせないのは、
   * `org_admin` が `org.write` を持たないため。相乗りさせると
   * 「管理者なのに問い合わせを送れない」という不合理が生まれる。
   */
  supportRead: 'org.support.read',
  supportWrite: 'org.support.write',

  /* --- 配信コンテンツ CMS（CMS_DESIGN.md §5.2） --- */
  contentRead: 'org.content.read',
  contentWrite: 'org.content.write',
  /**
   * リビジョンの承認。**トークンに付与できない**（`TOKEN_FORBIDDEN_PERMISSIONS`）。
   * 承認は「人が読んで責任を取った」ことの記録であり、API から押せる承認は承認ではない。
   */
  contentApprove: 'org.content.approve',
  /** 画像・ファイルのアップロードと差し替え。 */
  mediaWrite: 'org.media.write',

  /* --- 大量配信 --- */
  campaignRead: 'org.campaign.read',
  campaignWrite: 'org.campaign.write',
  /**
   * 配信の解禁・開始・再開。**トークンに付与できない**。
   * 10 万通は撤回できないので「盗まれたトークン 1 本の被害が事業ごと焼く操作」に該当する。
   */
  campaignSend: 'org.campaign.send',
  /** 配信の一時停止・中断。**止める操作なので広く開ける**（トークンにも許す）。 */
  campaignHalt: 'org.campaign.halt',
  /** 送信ストリーム（出口 IP の割り当て）の設定。 */
  sendingStreamWrite: 'org.sending_stream.write',

  /* --- 宛先台帳とサプレッション --- */
  contactRead: 'org.contact.read',
  contactWrite: 'org.contact.write',
  suppressionRead: 'org.suppression.read',
  suppressionWrite: 'org.suppression.write',

  /* --- Webhook --- */
  webhookRead: 'org.webhook.read',
  /**
   * Webhook の登録・変更。**トークンに付与できない**。
   * エンドポイントの登録は「データの持ち出し先を増やす操作」であり、
   * `contact.unsubscribed` を購読すれば購読者のメールアドレスが任意の URL へ流れる。
   * `apiTokenWrite`（トークンからトークンを増やす）を禁じているのと同型の穴。
   */
  webhookWrite: 'org.webhook.write',
} as const;

export type OrgPermission = (typeof ORG_PERMISSION)[keyof typeof ORG_PERMISSION];

export type Permission = OpsPermission | OrgPermission;

export const OPS_PERMISSIONS = Object.values(OPS_PERMISSION) as readonly OpsPermission[];
export const ORG_PERMISSIONS = Object.values(ORG_PERMISSION) as readonly OrgPermission[];
export const ALL_PERMISSIONS: readonly Permission[] = [...OPS_PERMISSIONS, ...ORG_PERMISSIONS];

/* -------------------------------------------------------- マトリクス */

const P = OPS_PERMISSION;
const O = ORG_PERMISSION;

/** 全 ops ロールが持つ読み取り権限。auditor の基準線でもある。 */
const OPS_READ_ONLY: readonly OpsPermission[] = [
  P.nodeRead,
  P.orgRead,
  P.planRead,
  P.billingRead,
  P.usageRead,
  P.jobRead,
  P.updateRead,
  P.auditRead,
  P.staffRead,
  P.securityRead,
  P.backupRead,
  P.webmailRead,
  P.certRead,
  P.settingsRead,
  // 問い合わせは読みだけ全ロールへ開ける（auditor が「期限内に答えたか」を
  // 確かめられないと、苦情処理体制の監査が成り立たない）。
  P.supportTicketRead,
];

/**
 * `operator` は日常運用の全書き込みを持つが、**秘密鍵閲覧とスタッフ管理は持たない**。
 * この 2 つを分けておくことで「運用担当者アカウントの乗っ取り = 全顧客の
 * DKIM 署名鍵の漏洩」にならない。
 */
const OPERATOR_EXTRA: readonly OpsPermission[] = [
  P.nodeWrite,
  P.nodeCommand,
  P.orgWrite,
  P.orgSuspend,
  P.supportMailboxReset,
  P.planWrite,
  P.billingWrite,
  P.jobWrite,
  P.updatePublish,
  P.updateHalt,
  P.auditVerify,
  P.impersonate,
  P.securityWrite,
  P.backupRestore,
  P.webmailWrite,
  P.certWrite,
  P.bulkHalt,
  P.contentTemplateWrite,
  P.supportTicketWrite,
];

/** `support` は顧客支援に必要な最小限の書き込みだけ持つ。 */
const SUPPORT_EXTRA: readonly OpsPermission[] = [
  P.supportMailboxReset,
  // 問い合わせに答えるのが support ロールの本務。
  P.supportTicketWrite,
  // 障害を「止める」操作は広く許す。止められないことのほうが危険なため。
  P.updateHalt,
  // 暴走した大量配信も同じ理由で support から止められるようにする。
  P.bulkHalt,
  /*
   * 本人からの権利行使のご請求（開示・訂正・利用停止・消去・可搬）の**受付**。
   *
   * これまで `privacyRequestWrite` は実効的に superadmin 専用で、実務で
   * 受付を担う support ロールは台帳に 1 行も書けなかった。個人情報保護法の
   * 権利行使対応には「遅滞なく」という時間的制約があり、掲示側でも
   * 「受付から `DATA_SUBJECT_REQUEST_DUE_DAYS` 日以内」と約束している
   * （プライバシーポリシー §6）。superadmin が不在・休職・退職中だと
   * **その約束を果たす手段が無い**状態だった。
   *
   * 受付・本人確認の記録は support の本務なので許す。開示の**実行**
   * （契約者データの中身を読む操作）は別の統制で守られている
   * （break-glass / `secretRead` 系）ので、ここを開けても中身は読めない。
   */
  P.privacyRequestWrite,
];

export const ROLE_PERMISSIONS: Readonly<Record<StaffRole | OrgRole, readonly Permission[]>> = {
  /* --- ops --- */
  superadmin: [...OPS_PERMISSIONS],
  operator: [...OPS_READ_ONLY, ...OPERATOR_EXTRA],
  support: [...OPS_READ_ONLY, ...SUPPORT_EXTRA],
  // auditor は完全読み取り + チェーン検証。impersonate も秘密閲覧も不可。
  auditor: [...OPS_READ_ONLY, P.auditVerify],

  /* --- portal --- */
  org_owner: [...ORG_PERMISSIONS],
  org_admin: [
    O.read,
    O.memberRead,
    O.domainRead,
    O.domainWrite,
    O.mailboxRead,
    O.mailboxWrite,
    O.aliasRead,
    O.aliasWrite,
    O.logRead,
    O.securityRead,
    O.usageRead,
    O.jobRead,
    O.billingRead,
    O.apiTokenRead,
    O.exportWrite,
    O.supportRead,
    O.supportWrite,
    // CMS と配信は org_admin の日常業務。ただし Webhook の登録
    // （データの持ち出し先を増やす操作）だけは org_owner に留める。
    O.contentRead,
    O.contentWrite,
    O.contentApprove,
    O.mediaWrite,
    O.campaignRead,
    O.campaignWrite,
    O.campaignSend,
    O.campaignHalt,
    O.sendingStreamWrite,
    O.contactRead,
    O.contactWrite,
    O.suppressionRead,
    O.suppressionWrite,
    O.webhookRead,
  ],
  /**
   * `org_member` は「自分のメールボックス」しか触れない。
   * 行レベルの絞り込みは `requireSelfOrPerm` が別途行う（権限だけでは表現できない）。
   */
  org_member: [O.read, O.mailboxRead, O.mailboxWrite, O.jobRead],
};

/* -------------------------------------------------------- 判定ヘルパ */

const ROLE_PERMISSION_SETS = new Map<string, ReadonlySet<Permission>>(
  Object.entries(ROLE_PERMISSIONS).map(([role, perms]) => [role, new Set(perms)]),
);

/** ロールが持つ権限集合。未知のロールは空集合（フェイルクローズ）。 */
export function permissionsForRole(role: string): ReadonlySet<Permission> {
  return ROLE_PERMISSION_SETS.get(role) ?? new Set<Permission>();
}

export function hasPerm(role: string, permission: Permission): boolean {
  return permissionsForRole(role).has(permission);
}

/** 与えられた権限集合に不足しているものを返す。空配列なら通過。 */
export function missingPermissions(
  granted: ReadonlySet<Permission>,
  required: readonly Permission[],
): Permission[] {
  return required.filter((p) => !granted.has(p));
}

/**
 * 組織 API トークンに**付与できない**権限（§7.1）。
 * トークンからトークンを増やす／課金を動かす／メンバーを増やす経路を断つ。
 */
export const TOKEN_FORBIDDEN_PERMISSIONS: readonly OrgPermission[] = [
  O.memberWrite,
  O.billingManage,
  O.apiTokenWrite,
  // 以下は CMS / 配信で追加した 3 つ。判断基準は上と同じ
  // 「盗まれたトークン 1 本の被害が取り返しのつかない操作」。
  O.campaignSend,
  O.contentApprove,
  O.webhookWrite,
  /*
   * **`org.write` は「本文の持ち出し先」を書き換えられる。**
   * `PUT /portal/ai/policy` はこの権限だけで Webメール AI の接続先
   * （`endpoint` / `preset` / `apiKey`）を差し替えられ、以後 AI 機能を
   * 使ったメール**本文**がその先へ流れる。`PUT /portal/privacy` も同じ
   * 権限でリモート画像プロキシ（トラッキング除去）を無効化できる。
   *
   * `webhookWrite` を禁じた理由（「データの持ち出し先を増やす操作」）と
   * 同じ判断で、しかもあちらが運ぶのはイベントのメタ、こちらは本文なので
   * より重い。**Webhook だけ禁止で AI が素通り**という非対称を消す。
   *
   * 統合面（`/content` `/delivery`）に `org.write` を要求するルートは
   * 1 本も無いので、機械的な連携が壊れることはない。
   */
  O.write,
];

export function isTokenGrantable(permission: string): permission is OrgPermission {
  if (!(ORG_PERMISSIONS as readonly string[]).includes(permission)) return false;
  return !(TOKEN_FORBIDDEN_PERMISSIONS as readonly string[]).includes(permission);
}

/**
 * impersonation では**常に**不可な権限（§7.2）。
 * 代理ログイン中に課金手段を差し替えられると、事後に本人が否認できない。
 */
export const IMPERSONATION_FORBIDDEN_PERMISSIONS: readonly OrgPermission[] = [
  O.billingManage,
  O.apiTokenWrite,
  // 代理ログイン中に 10 万通を送れてしまうと、事後に本人が否認できない。
  // 承認も同じで、「運営者が顧客の名前で承認した」記録は証跡として無価値になる。
  O.campaignSend,
  O.contentApprove,
  // **通信の秘密（電気通信事業法 4 条）に直結するのでここが最重要。**
  // `org.mailbox.write` にはメールボックスのパスワード設定が含まれる。
  // 代理ログイン中にこれを許すと、運営者が任意のパスワードを設定して
  // IMAP / Webmail で受信箱の本文をそのまま読める経路が開く。
  // 代理ログインは「顧客の画面を顧客と同じように見る」ためのものであって、
  // 資格情報を作り替えるためのものではない。
  O.mailboxWrite,
  // 招待・ロール変更で「運営者が自分用の常設アカウントを顧客組織に作る」ことを塞ぐ。
  // 一度作られると代理ログインの記録も期限も一切効かなくなる。
  O.memberWrite,
];

/**
 * 代理ログイン中に付与する契約者ロール（§7.2）。
 *
 * **JWT の `roles` クレームからではなく、この定数から引く。**
 * トークン発行側の指定をそのまま実効権限にすると、発行経路が 1 つ増えた瞬間に
 * `org_owner` を載せられて課金・Webhook まで届く。付与元を 1 か所に固定しておく。
 */
export const IMPERSONATION_ROLE = 'org_admin' as const;

/** `requireFreshAuth(300)` の対象（§7.2）。 */
export const FRESH_AUTH_PERMISSIONS: readonly Permission[] = [
  P.secretRead,
  P.staffWrite,
  P.nodeDelete,
  P.settingsWrite,
  O.billingManage,
  // break-glass での本文閲覧。秘密鍵の閲覧と同格に扱う。
  P.contentReadBody,
];

/** fresh-auth の既定の許容秒数。 */
export const FRESH_AUTH_MAX_AGE_SEC = 300;

export function requiresFreshAuth(required: readonly Permission[]): boolean {
  return required.some((p) => (FRESH_AUTH_PERMISSIONS as readonly string[]).includes(p));
}
